import React, { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { REVIEWS } from "@/data/site";

const Reviews = () => {
  const [idx, setIdx] = useState(0);
  const total = REVIEWS.length;
  const r = REVIEWS[idx];

  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);

  useEffect(() => {
    const id = setInterval(next, 5500);
    return () => clearInterval(id);
  }, [next]);

  // Show 8 dots max for aesthetic
  const dotsToShow = Math.min(8, total);
  const dots = Array.from({ length: dotsToShow }, (_, i) => i);
  const activeDot = Math.floor((idx / total) * dotsToShow);

  return (
    <section data-testid="reviews-section" className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-28">
      <div className="mx-auto max-w-3xl text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-[var(--gold)]/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-[var(--royal-deep)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--gold)]"></span> Guest stories
        </div>
        <h2 className="font-display text-3xl font-bold text-[var(--royal-deep)] sm:text-5xl">
          Loved across <span className="text-gradient-gold">Andhra Pradesh</span>
        </h2>
        <p className="mt-4 text-sm text-muted-foreground">
          {total}+ verified reviews · 4.9 average rating across our four service hubs.
        </p>
      </div>

      <div className="mx-auto mt-12 max-w-3xl">
        <div data-testid={`review-card-${idx}`} className="relative rounded-3xl bg-white p-8 shadow-[0_20px_60px_-25px_rgba(15,23,80,0.35)] ring-1 ring-[var(--royal)]/10 sm:p-12">
          <Quote className="absolute -top-5 left-8 h-12 w-12 rounded-full bg-[var(--gold)] p-3 text-[var(--royal-deep)]" />
          <p className="font-display text-xl leading-relaxed text-[var(--royal-deep)] sm:text-2xl">"{r.text}"</p>
          <div className="mt-6 flex items-center justify-between">
            <div>
              <div className="font-semibold text-[var(--royal-deep)]">{r.name}</div>
              <div className="text-sm text-muted-foreground">{r.city}</div>
            </div>
            <div className="flex gap-1 text-[var(--gold)]">
              {Array.from({ length: r.rating }).map((_, i) => (
                <Star key={i} size={20} className="fill-current" />
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-4">
          <button
            data-testid="reviews-prev-btn"
            aria-label="Previous"
            onClick={prev}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--royal)]/15 bg-white text-[var(--royal-deep)] hover:bg-[var(--gold)]/10"
          >
            <ChevronLeft size={20} />
          </button>
          <div className="flex gap-2">
            {dots.map((i) => (
              <button
                key={i}
                aria-label={`Go to ${i + 1}`}
                onClick={() => setIdx(Math.round((i / dotsToShow) * total))}
                className={`h-2 rounded-full transition-all ${i === activeDot ? "w-8 bg-[var(--gold)]" : "w-2 bg-[var(--royal)]/20"}`}
              />
            ))}
          </div>
          <button
            data-testid="reviews-next-btn"
            aria-label="Next"
            onClick={next}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[var(--royal)]/15 bg-white text-[var(--royal-deep)] hover:bg-[var(--gold)]/10"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        <p className="mt-4 text-center text-xs text-muted-foreground">
          Review {idx + 1} of {total}
        </p>
      </div>
    </section>
  );
};

export default Reviews;
